import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, NavLink, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import Avatar from "./Avatar";
import BrandLogo from "./BrandLogo";
import { classNames } from "../lib/utils";
import { Menu, X, ChevronDown, LogOut, User as UserIcon, ArrowLeft, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { NAV_CONFIG, STAFF_NAV, filterNavForUser } from "../lib/nav.config";

/* ─── Route guard (unchanged) ────────────────────────────────────── */
export function ProtectedRoute({ children, allow }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500"><div className="animate-pulse text-sm">Loading…</div></div>;
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (allow && !allow.includes(user.access_role)) {
    if (user.access_role === "Staff") return <Navigate to="/me" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

/* ─── Single nav item ──────────────────────────────────────────── */
function NavItem({ item, onNavigate, collapsed }) {
  const idSlug = item.label.toLowerCase().replace(/\s+/g, "-");
  const [tip, setTip] = useState(null);
  const showTip = (e) => { const r = e.currentTarget.getBoundingClientRect(); setTip({ top: r.top + r.height / 2, left: r.right + 10 }); };
  const hideTip = () => setTip(null);
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      end={item.to === "/dashboard"}
      data-testid={`nav-${idSlug}`}
      aria-label={item.label}
      onMouseEnter={collapsed ? showTip : undefined}
      onMouseLeave={collapsed ? hideTip : undefined}
      onFocus={collapsed ? showTip : undefined}
      onBlur={collapsed ? hideTip : undefined}
      className={({ isActive }) => classNames(
        "group relative flex items-center gap-3 rounded-lg text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400",
        collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2",
        isActive
          ? "bg-sky-50 text-sky-800 font-semibold shadow-[inset_3px_0_0_0_rgb(2_132_199)]"
          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
      )}
    >
      {({ isActive }) => (
        <>
          <item.icon size={collapsed ? 20 : 17} className={classNames("shrink-0 transition-colors", isActive ? "text-sky-700" : "text-slate-400 group-hover:text-slate-600")} />
          {collapsed && tip && (
            <span role="tooltip" style={{ top: tip.top, left: tip.left, transform: "translateY(-50%)" }} className="pointer-events-none fixed z-[70] whitespace-nowrap rounded-md bg-slate-900 text-white text-[13px] font-medium px-2.5 py-1.5 shadow-lg" data-testid={`nav-tooltip-${idSlug}`}>
              {item.label}
            </span>
          )}
          {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
          {!collapsed && item.badge != null && (
            <span className={classNames(
              "text-[11.5px] font-bold rounded-full px-1.5 py-0.5",
              isActive ? "bg-sky-200 text-sky-900" : "bg-slate-200 text-slate-700",
            )}>
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

/* ─── Group accordion ───────────────────────────────────────────── */
function NavGroup({ group, openGroup, onToggle, onNavigate, location, collapsed }) {
  const isOpen = openGroup === group.id;
  // If any item inside is the active route, force the group open
  const hasActive = useMemo(
    () => group.items.some((it) => location.pathname === it.to.split("?")[0]
                                   || location.pathname.startsWith(it.to.split("?")[0] + "/")),
    [group.items, location.pathname],
  );
  const showOpen = isOpen || hasActive;

  if (collapsed) {
    return (
      <div className="border-t border-slate-200 pt-2 mt-2 first:border-t-0 first:mt-0 first:pt-0 space-y-0.5 px-1.5" data-testid={`nav-group-${group.id}`} aria-label={group.label}>
        {group.items.map((it) => <NavItem key={it.to} item={it} onNavigate={onNavigate} collapsed />)}
      </div>
    );
  }

  return (
    <div className="border-t border-slate-100 first:border-t-0 pt-2 mt-2 first:mt-0 first:pt-0" data-testid={`nav-group-${group.id}`}>
      <button
        type="button"
        onClick={() => onToggle(group.id)}
        className="w-full flex items-center justify-between px-3 py-1.5 text-[11.5px] uppercase tracking-[0.12em] font-bold text-slate-400 hover:text-slate-600 transition-colors"
        data-testid={`nav-group-toggle-${group.id}`}
        aria-expanded={showOpen}
      >
        <span>{group.label}</span>
        <ChevronDown size={12} className={classNames("transition-transform duration-200", showOpen ? "rotate-0" : "-rotate-90")} />
      </button>
      <div
        className={classNames(
          "overflow-hidden transition-[max-height,opacity] duration-200 ease-out",
          showOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0",
        )}
      >
        <div className="space-y-0.5 px-1.5 pb-1">
          {group.items.map((it) => <NavItem key={it.to} item={it} onNavigate={onNavigate} />)}
        </div>
      </div>
    </div>
  );
}

/* ─── Sidebar body — header, nav, sticky user footer ───────────── */
function SidebarBody({ filteredNav, user, onNavigate, onLogout, openGroup, setOpenGroup, location, collapsed = false, onToggleCollapse }) {
  return (
    <>
      <div className={classNames("border-b border-slate-100 flex items-center bg-gradient-to-br from-sky-50/60 to-white", collapsed ? "flex-col gap-2 px-2 py-4" : "justify-between px-4 py-4")}>
        <Link to="/" onClick={onNavigate} className="flex items-center gap-2.5" aria-label="NestVillage home">
          <BrandLogo size={collapsed ? 32 : 36} />
          {!collapsed && (
            <div className="leading-tight">
              <p className="font-heading font-bold text-slate-900 text-[15.5px]">NestVillage</p>
              <p className="text-[12.5px] text-slate-500 font-medium">Care Metrics</p>
            </div>
          )}
        </Link>
        {onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            className="p-1.5 rounded-md text-slate-500 hover:bg-white hover:text-sky-800 border border-transparent hover:border-slate-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
            data-testid="sidebar-collapse-btn"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-pressed={collapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
        )}
      </div>

      <nav className={classNames("flex-1 py-3", collapsed ? "px-1 overflow-visible" : "px-2.5 overflow-y-auto")} data-testid="sidebar-nav-tree" data-collapsed={collapsed}>
        {filteredNav.map((group) => {
          if (group.kind === "primary") {
            return (
              <div key="primary" className={classNames("space-y-0.5 mb-2", collapsed && "px-1.5")}>
                {group.items.map((it) => <NavItem key={it.to} item={it} onNavigate={onNavigate} collapsed={collapsed} />)}
              </div>
            );
          }
          return (
            <NavGroup
              key={group.id}
              group={group}
              openGroup={openGroup}
              onToggle={setOpenGroup}
              onNavigate={onNavigate}
              location={location}
              collapsed={collapsed}
            />
          );
        })}
      </nav>

      {collapsed ? (
        <div className="p-2 border-t border-slate-100 flex flex-col items-center gap-2 bg-slate-50/50">
          <Link to={user.access_role === "Staff" ? "/me/profile" : "/profile"} onClick={onNavigate} title={`${user.full_name} · Profile`} aria-label="Profile" className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400" data-testid="sidebar-profile-btn">
            <Avatar name={user.full_name} gender={user.gender} src={user.profile_image} size="sm" />
          </Link>
          <button onClick={onLogout} data-testid="logout-btn" title="Sign out" aria-label="Sign out" className="p-2 rounded-lg text-slate-600 hover:bg-rose-50 hover:text-rose-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400">
            <LogOut size={17} />
          </button>
        </div>
      ) : (
      <div className="p-3 border-t border-slate-100 space-y-1.5 bg-slate-50/50">
        <Link
          to="/?landing=1"
          onClick={onNavigate}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 border border-slate-200 hover:border-sky-300 hover:bg-white hover:text-sky-800 hover:shadow-sm transition-all"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft size={14} className="shrink-0" />
          <span>Back to Home</span>
        </Link>
        <Link
          to={user.access_role === "Staff" ? "/me/profile" : "/profile"}
          onClick={onNavigate}
          className="w-full flex items-center gap-3 px-2.5 py-2 rounded-lg hover:bg-white hover:shadow-sm transition-all text-left border border-transparent hover:border-slate-200"
          data-testid="sidebar-profile-btn"
        >
          <Avatar name={user.full_name} gender={user.gender} src={user.profile_image} size="sm" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">{user.full_name}</p>
            <p className="text-[12.5px] text-slate-500 truncate">{user.access_role}</p>
          </div>
          <UserIcon size={13} className="text-slate-400" />
        </Link>
        <button
          onClick={onLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-rose-50 hover:text-rose-700 transition-colors"
        >
          <LogOut size={15} />
          <span>Sign out</span>
        </button>
      </div>
      )}
    </>
  );
}

/* ─── Layout component ───────────────────────────────────────────── */
export default function Layout({ children }) {
  const { user, logout, hasCapability } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("nv_sidebar_collapsed") === "1"; } catch { return false; }
  });
  const toggleCollapse = useCallback(() => {
    setCollapsed((c) => { try { localStorage.setItem("nv_sidebar_collapsed", c ? "0" : "1"); } catch {} return !c; });
  }, []);

  // Persist which group was last expanded across the session.
  const [openGroup, setOpenGroupRaw] = useState(() => {
    try { return sessionStorage.getItem("nv_nav_open_group") || "operations"; }
    catch { return "operations"; }
  });
  const setOpenGroup = useCallback((id) => {
    setOpenGroupRaw((prev) => {
      const next = prev === id ? null : id;
      try { sessionStorage.setItem("nv_nav_open_group", next || ""); } catch {}
      return next;
    });
  }, []);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [drawerOpen]);

  const filteredNav = useMemo(() => {
    if (!user) return [];
    const tree = user.access_role === "Staff" ? STAFF_NAV : NAV_CONFIG;
    return filterNavForUser(tree, user, { hasCapability });
  }, [user, hasCapability]);

  if (!user) return null;

  // Derive the page title for the mobile top bar from any matching nav item
  const allItems = filteredNav.flatMap((g) => g.items);
  const activeItem = allItems.find((it) => {
    const path = it.to.split("?")[0];
    return location.pathname === path || location.pathname.startsWith(path + "/");
  });
  const pageTitle = activeItem?.label || "NestVillage";

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <div className="min-h-screen lg:h-screen lg:overflow-hidden bg-slate-50 lg:flex">
      {/* Desktop sidebar (>= lg) — fills viewport height, does not scroll with
          the main content. Only its inner nav scrolls if it exceeds height. */}
      <aside
        className={classNames("hidden lg:flex lg:h-full shrink-0 border-r border-slate-200 bg-white flex-col transition-[width] duration-200 ease-out", collapsed ? "w-[68px]" : "w-[248px]")}
        data-testid="sidebar-nav"
        data-collapsed={collapsed}
      >
        <SidebarBody
          filteredNav={filteredNav} user={user}
          onNavigate={() => {}} onLogout={logout}
          openGroup={openGroup} setOpenGroup={setOpenGroup}
          location={location} collapsed={collapsed} onToggleCollapse={toggleCollapse}
        />
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 px-4 h-14 flex items-center justify-between" data-testid="mobile-topbar">
        <button
          onClick={() => setDrawerOpen(true)}
          className="p-2 -ml-2 rounded-md text-slate-700 hover:bg-slate-100 active:bg-slate-200"
          data-testid="mobile-menu-btn"
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <BrandLogo size={26} />
          <p className="font-heading font-bold text-slate-900 text-sm truncate">{pageTitle}</p>
        </div>
        <button
          onClick={() => navigate(user.access_role === "Staff" ? "/me/profile" : "/profile")}
          className="p-1 -mr-1 rounded-full"
          data-testid="mobile-profile-btn"
          aria-label="Profile"
        >
          <Avatar name={user.full_name} gender={user.gender} src={user.profile_image} size="sm" />
        </button>
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm" onClick={closeDrawer} data-testid="mobile-drawer-backdrop" aria-hidden="true" />
      )}
      <aside
        className={classNames(
          "lg:hidden fixed top-0 left-0 z-50 h-full w-72 max-w-[82vw] bg-white border-r border-slate-200 flex flex-col shadow-2xl transition-transform duration-200 ease-out",
          drawerOpen ? "translate-x-0" : "-translate-x-full",
        )}
        data-testid="mobile-drawer"
        aria-hidden={!drawerOpen}
      >
        <button
          onClick={closeDrawer}
          className="absolute top-3 right-3 p-2 rounded-md text-slate-500 hover:bg-slate-100"
          data-testid="mobile-drawer-close-btn"
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
        <SidebarBody
          filteredNav={filteredNav} user={user}
          onNavigate={closeDrawer} onLogout={() => { closeDrawer(); logout(); }}
          openGroup={openGroup} setOpenGroup={setOpenGroup}
          location={location}
        />
      </aside>

      {/* Main content — on desktop this is the ONLY scrollable region. The
          right-hand scrollbar scrolls only this area; the sidebar stays fixed. */}
      <main
        className="flex-1 min-w-0 pb-20 lg:pb-0 lg:h-full lg:overflow-y-auto lg:overflow-x-hidden"
        data-testid="app-main"
      >
        {children}
      </main>
    </div>
  );
}
